import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * "Are you sure?" — as a modal, because that is the standard here.
 *
 * Ray, 2026-08-31: *"the gate message should be modal thats our standard."* The
 * customer delete used to drop an inline red panel BELOW the table, so on a list
 * of any length the confirmation appeared somewhere the operator was not
 * looking, about a row they could no longer see. A modal is the one shape that
 * cannot land off-screen.
 *
 * The destructive button is never the default focus and never on the left: this
 * dialog exists to be read, and a Yes sitting where Cancel usually is gets
 * clicked by muscle memory.
 */
interface Props {
   title: string;
   /** What is lost, and the gentler alternative if there is one. */
   message: string;
   confirmLabel: string;
   /**
    * Ask for a short note before confirming, and hand it back.
    *
    * Used by Suspend. Ray, 2026-09-02: a customer cut off by hand was told
    * their account was "fully settled" — true, useless, and unanswerable
    * because nothing recorded WHY. The note goes on their bill page.
    */
   notePrompt?: string;
   noteHint?: string;
   onConfirm: (note?: string) => void | Promise<void>;
   onCancel: () => void;
}

const IspConfirm: React.FC<Props> = ({
   title,
   message,
   confirmLabel,
   notePrompt,
   noteHint,
   onConfirm,
   onCancel,
}) => {
   const [busy, setBusy] = useState(false);
   const [note, setNote] = useState('');
   return (
      <Wrapper>
         <Title>{title}</Title>
         <Message>{message}</Message>
         {notePrompt ? (
            <NoteBox>
               <NoteLbl htmlFor="confirm-note">{notePrompt}</NoteLbl>
               <NoteInput
                  id="confirm-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
               />
               {noteHint ? <NoteHint>{noteHint}</NoteHint> : null}
            </NoteBox>
         ) : null}
         <Actions>
            <Ghost type="button" onClick={onCancel} disabled={busy}>
               Cancel
            </Ghost>
            <Danger
               type="button"
               disabled={busy}
               onClick={async () => {
                  setBusy(true);
                  try {
                     await onConfirm(note.trim() || undefined);
                  } finally {
                     setBusy(false);
                  }
               }}
            >
               {busy ? 'Working…' : confirmLabel}
            </Danger>
         </Actions>
      </Wrapper>
   );
};

const NoteBox = styled.div`
   margin-bottom: 16px;
`;
const NoteLbl = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 700;
   margin-bottom: 6px;
`;
const NoteInput = styled.input`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 14px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   background: transparent;
   color: inherit;
`;
const NoteHint = styled.div`
   margin-top: 5px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;

export default IspConfirm;

const Wrapper = styled.div`
   width: 460px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Title = styled.h2`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const Message = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.65;
   color: ${colors.GRAY5};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 6px;
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;

const Danger = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.DANGER};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;
